const parseRgba = (str) => {
	const m = str && str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
	return { r: 0, g: 0, b: 0, a: 1 };
};
const toRgba = (r, g, b, a) => `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`;

/** Fondo del bloque quiz en juego/editor; prioriza content.quizBackgroundRgba. */
const getQuizBlockBackground = (element) => {
	const c = element?.content?.quizBackgroundRgba?.trim();
	if (c) return c;
	const s = element?.style?.backgroundColor;
	if (!s || s === "transparent") return "rgba(255,255,255,1)";
	if (String(s).startsWith("rgba(")) return s;
	if (String(s).startsWith("rgb(")) return String(s).replace(/^rgb\(/, "rgba(").replace(/\)$/, ", 1)");
	if (String(s).startsWith("#")) {
		const h = String(s).slice(1);
		const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
		const n = parseInt(x, 16);
		if (Number.isNaN(n)) return "rgba(255,255,255,1)";
		return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},1)`;
	}
	return "rgba(255,255,255,1)";
};

/** Normaliza color (hex, rgb, rgba, transparent) a rgba para RgbaColorPickerPopover */
const normalizeColorToRgba = (s, defaultAlpha = 1) => {
	if (!s || s === "transparent") return defaultAlpha === 0 ? "rgba(255,255,255,0)" : "rgba(0,0,0,1)";
	if (String(s).startsWith("rgba(")) return s;
	if (String(s).startsWith("rgb(")) return String(s).replace(/^rgb\(/, "rgba(").replace(/\)$/, ", 1)");
	if (String(s).startsWith("#")) {
		const h = String(s).slice(1);
		const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
		const n = parseInt(x, 16);
		if (Number.isNaN(n)) return "rgba(0,0,0,1)";
		return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${defaultAlpha})`;
	}
	return "rgba(0,0,0,1)";
};

const DEFAULT_QUIZ_BORDER_RGBA = "rgba(226,232,240,1)";
const getQuizBlockBorderCss = (element) => {
	const content = element?.content || {};
	const wRaw = content.quizBorderWidth;
	const width = wRaw === undefined || wRaw === null ? 1 : Math.max(0, Math.min(10, Number(wRaw)));
	if (!Number.isFinite(width) || width <= 0) return "none";
	const c = String(content.quizBorderRgba || "").trim();
	let color = DEFAULT_QUIZ_BORDER_RGBA;
	if (c) {
		if (/^rgba\(/i.test(c)) color = c;
		else if (/^rgb\(/i.test(c)) color = c.replace(/^rgb\(/i, "rgba(").replace(/\)$/, ", 1)");
		else if (c.startsWith("#")) {
			const h = c.slice(1);
			const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
			const n = parseInt(x, 16);
			if (!Number.isNaN(n)) color = `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},1)`;
		}
	}
	return `${width}px solid ${color}`;
};
const getQuizBlockBorderColorForPicker = (element) => {
	const c = String(element?.content?.quizBorderRgba || "").trim();
	if (!c) return DEFAULT_QUIZ_BORDER_RGBA;
	if (/^rgba?\(/i.test(c)) return c.startsWith("rgb(") ? c.replace(/^rgb\(/i, "rgba(").replace(/\)$/, ", 1)") : c;
	if (c.startsWith("#")) {
		const h = c.slice(1);
		const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
		const n = parseInt(x, 16);
		if (!Number.isNaN(n)) return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},1)`;
	}
	return DEFAULT_QUIZ_BORDER_RGBA;
};
const getQuizBlockBorderWidth = (element) => {
	const w = element?.content?.quizBorderWidth;
	if (w === undefined || w === null) return 1;
	const n = Number(w);
	return Number.isFinite(n) ? Math.max(0, Math.min(10, n)) : 1;
};

const DEFAULT_QUIZ_BLOCK_BOX_SHADOW = "0 2px 8px rgba(0,0,0,0.08)";

const getQuizBlockShadowBlur = (element) => {
	const w = element?.content?.quizBlockShadowBlur;
	if (w === undefined || w === null || w === "") return 8;
	const n = Number(w);
	return Number.isFinite(n) ? Math.max(0, Math.min(24, n)) : 8;
};

const getQuizBlockBoxShadow = (element) => {
	const content = element?.content || {};
	const blurRaw = content.quizBlockShadowBlur;
	const opRaw = content.quizBlockShadowOpacity;
	const blurExplicit = blurRaw !== undefined && blurRaw !== null && blurRaw !== "";
	const opacityExplicit = opRaw != null && opRaw !== "" && Number.isFinite(Number(opRaw));
	if (!blurExplicit && !opacityExplicit) return DEFAULT_QUIZ_BLOCK_BOX_SHADOW;
	const blur = blurExplicit ? Number(blurRaw) : 8;
	if (!Number.isFinite(blur) || blur <= 0) return "none";
	const b = Math.max(1, Math.min(24, blur));
	const alpha = opacityExplicit ? Math.max(0, Math.min(100, Number(opRaw))) / 100 : 0.08;
	return `0 2px ${b}px rgba(0,0,0,${alpha})`;
};

const QUIZ_OPTION_DESIGNS = {
	classic: { layout: "default", padding: "10px 14px", borderRadius: 8, borderWidth: 1, defaultBg: "#fff", defaultBorder: "#e2e8f0", correctBg: "#f0fdf4", correctBorder: "#22c55e", incorrectBg: "#fef2f2", incorrectBorder: "#ef4444", boxShadow: "none", iconPosition: "both" },
	letter: { layout: "letter", padding: "16px 20px", borderRadius: 16, borderWidth: 1, defaultBg: "#fff", defaultBorder: "#e2e8f0", correctBg: "#ecfdf5", correctBorder: "#10b981", incorrectBg: "#fef2f2", incorrectBorder: "#ef4444", boxShadow: "0 4px 12px rgba(0,0,0,0.06)", iconPosition: "right", letterCircleDefaultBg: "#f1f5f9", letterCircleDefaultColor: "#475569", letterCircleCorrectBg: "#10b981", letterCircleIncorrectBg: "#ef4444" },
	segmented: { layout: "segmented", padding: "14px 20px", borderRadius: 12, borderWidth: 1, defaultBg: "#f8fafc", defaultBorder: "#e2e8f0", correctBg: "#dcfce7", correctBorder: "#22c55e", incorrectBg: "#fee2e2", incorrectBorder: "#ef4444", boxShadow: "none", iconPosition: "right", segmentGap: 0 },
};
function hexToRgbaQuiz(hex, alpha) {
	if (!hex || typeof hex !== "string") return undefined;
	const h = hex.replace(/^#/, "");
	const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
	const n = parseInt(x, 16);
	if (Number.isNaN(n)) return undefined;
	return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}
function getQuizTrueFalseColorsEditor(element) {
	const content = element?.content || {};
	const rawTrue = (content.quizTrueColor && String(content.quizTrueColor).trim()) || "";
	const rawFalse = (content.quizFalseColor && String(content.quizFalseColor).trim()) || "";
	const hexLike = (s) => /^#?[a-fA-F0-9]{6}$/.test(s) || (s.length === 6 && /^[a-fA-F0-9]+$/.test(s));
	const trueColor = hexLike(rawTrue) ? (rawTrue.startsWith("#") ? rawTrue : `#${rawTrue}`) : "#22c55e";
	const falseColor = hexLike(rawFalse) ? (rawFalse.startsWith("#") ? rawFalse : `#${rawFalse}`) : "#ef4444";
	return { trueColor, falseColor, trueBg: hexToRgbaQuiz(trueColor, 1) || "#f0fdf4", falseBg: hexToRgbaQuiz(falseColor, 1) || "#fef2f2" };
}
function getQuizOptionStyles(element, state, context = {}) {
	const c = element?.content?.quizOptionsCustom || {};
	const designId = element?.content?.quizOptionsDesign || "classic";
	const design = QUIZ_OPTION_DESIGNS[designId] || QUIZ_OPTION_DESIGNS.classic;
	const questionType = context.questionType;
	const isTrueFalse = questionType === "trueFalse";
	const hasDefinedTfColors = !!(element?.content?.quizTrueColor || element?.content?.quizFalseColor);
	const applyTfColors = isTrueFalse && hasDefinedTfColors;
	const tfColors = getQuizTrueFalseColorsEditor(element);
	const styleColorRaw = element?.content?.quizOptionsStyle?.color;
	const styleColor = styleColorRaw && String(styleColorRaw).trim() ? String(styleColorRaw).trim() : "";
	const designMerged = {
		...design,
		defaultOptionColor: styleColor || design.defaultOptionColor,
		correctBg: applyTfColors && element?.content?.quizTrueColor && tfColors.trueBg ? tfColors.trueBg : design.correctBg,
		correctBorder: applyTfColors && element?.content?.quizTrueColor && tfColors.trueColor ? tfColors.trueColor : design.correctBorder,
		incorrectBg: applyTfColors && element?.content?.quizFalseColor && tfColors.falseBg ? tfColors.falseBg : design.incorrectBg,
		incorrectBorder: applyTfColors && element?.content?.quizFalseColor && tfColors.falseColor ? tfColors.falseColor : design.incorrectBorder,
		letterCircleCorrectBg: applyTfColors && element?.content?.quizTrueColor && tfColors.trueColor ? tfColors.trueColor : design.letterCircleCorrectBg,
		letterCircleIncorrectBg: applyTfColors && element?.content?.quizFalseColor && tfColors.falseColor ? tfColors.falseColor : design.letterCircleIncorrectBg,
	};
	if (c.optionBg && String(c.optionBg).trim()) designMerged.defaultBg = String(c.optionBg).trim();
	if (c.optionBorderColor && String(c.optionBorderColor).trim()) designMerged.defaultBorder = String(c.optionBorderColor).trim();
	if (c.optionBorderWidth != null && c.optionBorderWidth !== "" && Number.isFinite(Number(c.optionBorderWidth))) {
		designMerged.borderWidth = Math.max(0, Math.min(10, Number(c.optionBorderWidth)));
	}
	const { done, isSelected, correct, sel } = state;
	const { index = 0, totalCount = 1 } = context;
	const hasCustomTfColors = applyTfColors;
	let bg = designMerged.defaultBg;
	let border = designMerged.defaultBorder;
	let optionColor = designMerged.defaultOptionColor;
	let borderRadius = designMerged.borderRadius;
	let boxShadow = designMerged.boxShadow;
	let borderBottomWidth = designMerged.borderBottomWidth;
	let borderBottomColor = designMerged.correctBorderBottomColor;
	if (hasCustomTfColors) {
		if (correct) { bg = designMerged.correctBg; border = designMerged.correctBorder; optionColor = designMerged.correctOptionColor || optionColor; boxShadow = designMerged.correctBoxShadow || boxShadow; borderBottomWidth = designMerged.correctBorderBottomWidth; borderBottomColor = designMerged.correctBorderBottomColor; }
		else { bg = designMerged.incorrectBg; border = designMerged.incorrectBorder; optionColor = designMerged.incorrectOptionColor || optionColor; boxShadow = designMerged.incorrectBoxShadow || boxShadow; borderBottomWidth = designMerged.incorrectBorderBottomWidth; borderBottomColor = designMerged.incorrectBorderBottomColor; }
	} else if (done || isSelected) {
		if (correct) { bg = designMerged.correctBg; border = designMerged.correctBorder; optionColor = designMerged.correctOptionColor || optionColor; boxShadow = designMerged.correctBoxShadow || boxShadow; borderBottomWidth = designMerged.correctBorderBottomWidth; borderBottomColor = designMerged.correctBorderBottomColor; }
		else if (isSelected && sel && !sel.correct) { bg = designMerged.incorrectBg; border = designMerged.incorrectBorder; optionColor = designMerged.incorrectOptionColor || optionColor; boxShadow = designMerged.incorrectBoxShadow || boxShadow; borderBottomWidth = designMerged.incorrectBorderBottomWidth; borderBottomColor = designMerged.incorrectBorderBottomColor; }
	}
	const hasCustomBorderRadius = c.optionBorderRadius != null && c.optionBorderRadius !== "" && Number.isFinite(Number(c.optionBorderRadius));
	if (hasCustomBorderRadius) {
		borderRadius = Math.max(0, Math.min(50, Number(c.optionBorderRadius)));
	} else if (designMerged.layout === "segmented" && totalCount > 1) {
		borderRadius = index === 0 ? "12px 0 0 12px" : index === totalCount - 1 ? "0 12px 12px 0" : 0;
	}
	const hoverBgCustom = (c.optionBgHover && String(c.optionBgHover).trim()) || null;
	const hoverTextCustom = (element?.content?.quizOptionsStyle?.hoverColor && String(element.content.quizOptionsStyle.hoverColor).trim()) || null;
	return { layout: designMerged.layout || "default", backgroundColor: bg, borderColor: border, borderWidth: designMerged.borderWidth, borderRadius, padding: designMerged.padding, boxShadow: boxShadow || undefined, iconLeft: designMerged.iconPosition === "left" || designMerged.iconPosition === "both", iconRight: designMerged.iconPosition === "right" || designMerged.iconPosition === "both", optionColor: optionColor || undefined, hoverBackgroundColor: hoverBgCustom || undefined, hoverOptionColor: hoverTextCustom || undefined, borderBottomWidth: borderBottomWidth != null ? borderBottomWidth : undefined, borderBottomColor: borderBottomColor || undefined, letterCircleDefaultBg: designMerged.letterCircleDefaultBg, letterCircleDefaultColor: designMerged.letterCircleDefaultColor, letterCircleCorrectBg: designMerged.letterCircleCorrectBg, letterCircleIncorrectBg: designMerged.letterCircleIncorrectBg, radioSize: designMerged.radioSize, trueColor: tfColors.trueColor, falseColor: tfColors.falseColor, applyTfColorsByDefault: !!hasCustomTfColors };
}

function normalizeQuizOptionExtraBoxShadow(extraBoxShadow) {
	if (!extraBoxShadow || extraBoxShadow === "none") return undefined;
	return extraBoxShadow;
}

function resolveQuizOptionBorderWidth(borderWidth, fallback = 1) {
	if (borderWidth != null && borderWidth !== "" && Number.isFinite(Number(borderWidth))) {
		return Math.max(0, Math.min(10, Number(borderWidth)));
	}
	return fallback;
}

function resolveQuizOptionBorderPresentation(borderWidth, borderColor, extraBoxShadow) {
	const w = resolveQuizOptionBorderWidth(borderWidth);
	const extra = normalizeQuizOptionExtraBoxShadow(extraBoxShadow);
	if (!w) {
		return { border: "none", boxShadow: extra };
	}
	const col = String(borderColor || "").trim() || "#e2e8f0";
	return {
		border: `${w}px solid ${col}`,
		boxShadow: extra,
	};
}

function resolveQuizOptionBorderCss(borderWidth, borderColor) {
	const w = resolveQuizOptionBorderWidth(borderWidth);
	if (!w) return "none";
	const col = String(borderColor || "").trim() || "#e2e8f0";
	return `${w}px solid ${col}`;
}

const parseColorToRgb = (str) => {
	if (!str || str === "transparent") return null;
	const rgba = str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (rgba) return { r: +rgba[1], g: +rgba[2], b: +rgba[3], a: rgba[4] !== undefined ? +rgba[4] : 1 };
	const hex = str.match(/^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/);
	if (hex) {
		let h = hex[1];
		if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
		return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
	}
	return null;
};

const getDefaultHoverColor = (baseColor) => {
	const rgb = parseColorToRgb(baseColor);
	if (!rgb) return undefined;
	const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
	if (luminance < 100) {
		return toRgba(Math.min(255, rgb.r + 45), Math.min(255, rgb.g + 45), Math.min(255, rgb.b + 45), rgb.a);
	}
	return toRgba(Math.max(0, rgb.r - 30), Math.max(0, rgb.g - 30), Math.max(0, rgb.b - 30), rgb.a);
};

const isShapeGradient = (v) => v && String(v).trim().startsWith("linear-gradient");

function mergeTextAnswerBoxShadow(base, extra) {
	if (base && extra && base !== "none" && extra !== "none") return `${base}, ${extra}`;
	if (base && base !== "none") return base;
	if (extra && extra !== "none") return extra;
	return undefined;
}

function buildTextAnswerFieldPresentation({
	element,
	optionTextStyle,
	optionBoxFx = {},
	state = {},
	context = {},
}) {
	const { done = false, isSelected = false, correct = false, sel = null } = state;
	const optStyles = getQuizOptionStyles(element, { done, isSelected, correct, sel }, context);
	const optionBorderStyle = resolveQuizOptionBorderPresentation(
		optStyles.borderWidth,
		optStyles.borderColor,
		optStyles.boxShadow,
	);
	// Solo boxShadow estático: filter/glitch/CSS vars en inputs nativos generan marcos magenta y capas fantasma.
	const optionBoxFxShadow = optionBoxFx.extraStyle?.boxShadow;
	const isBigButtons = optStyles?.layout === "bigButtons";
	const containerStyle = {
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		width: "100%",
		alignSelf: "stretch",
		display: "flex",
		alignItems: isBigButtons ? "center" : "stretch",
		justifyContent: isBigButtons ? "center" : undefined,
		boxSizing: "border-box",
		overflow: "hidden",
		padding: optStyles?.padding,
		borderRadius: optStyles?.borderRadius,
		background: optStyles?.backgroundColor,
		...optionBorderStyle,
		boxShadow: mergeTextAnswerBoxShadow(optionBorderStyle.boxShadow, optionBoxFxShadow),
	};
	const inputStyle = {
		...optionTextStyle,
		color: optStyles?.optionColor || optionTextStyle?.color,
		flex: 1,
		minWidth: 0,
		minHeight: 0,
		width: "100%",
		height: "100%",
		alignSelf: "stretch",
		border: "none",
		background: "transparent",
		outline: "none",
		boxShadow: "none",
		borderRadius: "inherit",
		WebkitAppearance: "none",
		appearance: "none",
		WebkitTapHighlightColor: "transparent",
		boxSizing: "border-box",
	};
	const submitOptStyles = getQuizOptionStyles(
		element,
		{ done: false, isSelected: false, correct: false, sel: null },
		context
	);
	const submitBorderStyle = resolveQuizOptionBorderPresentation(
		submitOptStyles.borderWidth,
		submitOptStyles.borderColor,
		submitOptStyles.boxShadow
	);
	const submitButtonStyle = {
		background: submitOptStyles?.backgroundColor,
		color: submitOptStyles?.optionColor || optionTextStyle?.color,
		borderRadius: submitOptStyles?.borderRadius,
		padding: submitOptStyles?.padding ?? "0 16px",
		boxSizing: "border-box",
		WebkitAppearance: "none",
		appearance: "none",
		...submitBorderStyle,
		boxShadow: mergeTextAnswerBoxShadow(submitBorderStyle.boxShadow, optionBoxFxShadow),
	};
	return {
		containerStyle,
		inputStyle,
		className: undefined,
		submitButtonStyle,
		submitButtonClassName: undefined,
	};
}

export {
	parseRgba,
	toRgba,
	getQuizBlockBackground,
	normalizeColorToRgba,
	DEFAULT_QUIZ_BORDER_RGBA,
	getQuizBlockBorderCss,
	getQuizBlockBorderColorForPicker,
	getQuizBlockBorderWidth,
	getQuizBlockShadowBlur,
	getQuizBlockBoxShadow,
	QUIZ_OPTION_DESIGNS,
	hexToRgbaQuiz,
	getQuizTrueFalseColorsEditor,
	getQuizOptionStyles,
	parseColorToRgb,
	getDefaultHoverColor,
	resolveQuizOptionBorderCss,
	resolveQuizOptionBorderPresentation,
	isShapeGradient,
	buildTextAnswerFieldPresentation,
};
